import * as React from 'react'
import { useCallback } from 'react'
import Typography from '@mui/material/Typography'
import { useNavigate } from 'react-router-dom'
import Stack from '@mui/material/Stack'
import IconButton from '@mui/material/IconButton'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { useTheme } from '@mui/material/styles'

// use the same helpers you use in the Manage card
import { SettingsRow } from './SettingsRow'
import { LeadingIcon } from './LeadIcon'

interface Props {
  description?: string
  onClick?: () => void
  path?: string
  title: string
  icon: React.ReactNode
  dataTestId?: string
}

export function SettingsHomeSubSectionTidal(props: Props) {
  const { description, onClick, path, title, dataTestId, icon } = props

  const theme = useTheme()
  const navigate = useNavigate()
  const onClickTitle = useCallback(() => {
    if (onClick) {
      return onClick()
    }
    if (path) {
      navigate(path)
    }
  }, [navigate, onClick, path])

  return (
    <SettingsRow
      left={
        <Stack
          direction="row"
          alignItems="center" // top-align when description wraps
          gap={{ xs: 1, sm: 2 }}
          sx={{ minWidth: 0 }} // allow wrapping
        >
          <LeadingIcon>{icon}</LeadingIcon>

          <Stack gap={0.5} sx={{ flex: 1, minWidth: 0 }}>
            {/* Title can wrap (no ellipsis) */}
            <Typography
              variant="h6"
              onClick={onClickTitle}
              data-test-id={dataTestId}
              sx={{
                color: theme.palette.primary.main,
                whiteSpace: 'normal', // ✅ wrap
                wordBreak: 'break-word',
                '&:hover': { cursor: 'pointer', textDecoration: 'underline' },
              }}
            >
              {title}
            </Typography>

            {/* Description fully visible (wraps) */}
            {description ? (
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.primary,
                  whiteSpace: 'normal', // ✅ wrap
                  wordBreak: 'break-word',
                }}
              >
                {description}
              </Typography>
            ) : null}
          </Stack>
        </Stack>
      }
      right={
        <IconButton
          size="small"
          onClick={onClickTitle}
          aria-label="Open section"
          sx={{ whiteSpace: 'nowrap' }} // keep chevron column stable
        >
          <ChevronRightIcon fontSize="medium" />
        </IconButton>
      }
    />
  )
}

SettingsHomeSubSectionTidal.displayName = 'SettingsHomeSubSectionTidal'
